<template>
  <DeliveryPromise :merchant :shipping>
    <template v-if="deliveryLabel" #title>
      <span data-allow-mismatch="text">{{ deliveryLabel }}</span>
    </template>
  </DeliveryPromise>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'

import DeliveryPromise from '~/scopes/shipping/components/DeliveryPromise/DeliveryPromise.vue'
import { useDeliveryDateAdjustment } from '~/scopes/shipping/composables/useDeliveryDateAdjustment/useDeliveryDateAdjustment'

import translations from './USDeliveryPromiseCard.translations'

const props = defineProps<{
  shipping: NonNullable<GetPickersResponse['selectedOffer']>['shipping']
  merchant: NonNullable<GetPickersResponse['selectedOffer']>['merchant']
}>()

const i18n = useI18n()
const currentLocale = useI18nLocale()
const experiments = useExperiments()
const deliveryDateAdjustment = useDeliveryDateAdjustment()

const deliveryLabel = computed(() => {
  const variant = experiments['experiment.usDeliveryDate']

  if (variant === 'withDayOfWeek') {
    if (!props.shipping?.free?.latestArrivalDate) return null

    const adjusted = deliveryDateAdjustment(
      props.shipping.free.earliestArrivalDate,
    )
    const tomorrow = new Date()
    tomorrow.setUTCDate(tomorrow.getUTCDate() + 1)

    const isTomorrow =
      adjusted.getUTCFullYear() === tomorrow.getUTCFullYear() &&
      adjusted.getUTCMonth() === tomorrow.getUTCMonth() &&
      adjusted.getUTCDate() === tomorrow.getUTCDate()

    if (isTomorrow) {
      return i18n(translations.deliveryByTomorrow)
    }

    return i18n(translations.deliveryDateWithDayOfWeek, {
      date: adjusted.toLocaleDateString(currentLocale, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      }),
    })
  }

  if (variant === 'withDayCount') {
    if (
      !props.shipping?.free?.earliestArrivalDate ||
      !props.shipping?.free?.latestArrivalDate
    )
      return null

    const adjustedEarliest = deliveryDateAdjustment(
      props.shipping.free.earliestArrivalDate,
    )
    const adjustedLatest = deliveryDateAdjustment(
      props.shipping.free.latestArrivalDate,
    )

    const today = new Date()
    const todayUTC = Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate(),
    )
    const msPerDay = 1000 * 60 * 60 * 24

    const minDays = Math.round(
      (Date.UTC(
        adjustedEarliest.getUTCFullYear(),
        adjustedEarliest.getUTCMonth(),
        adjustedEarliest.getUTCDate(),
      ) -
        todayUTC) /
        msPerDay,
    )
    const maxDays = Math.round(
      (Date.UTC(
        adjustedLatest.getUTCFullYear(),
        adjustedLatest.getUTCMonth(),
        adjustedLatest.getUTCDate(),
      ) -
        todayUTC) /
        msPerDay,
    )

    if (minDays === 0) {
      return maxDays === 1
        ? i18n(translations.deliveryInDay, { count: maxDays })
        : i18n(translations.deliveryInDays, { count: maxDays })
    }

    return i18n(translations.deliveryInDaysRange, { minDays, maxDays })
  }

  return null
})
</script>
